import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const STORAGE_KEY = "sidebarSection";
const COMPANY = "company";
const SUPPLIER = "supplier";
const isSupplierInstance = process.env.REACT_APP_INSTANCE_TYPE === "supplier";

const SidebarSectionContext = createContext({
  section: COMPANY,
  setSection: () => {},
  toggleSection: () => {},
  canToggle: false,
});

/**
 * Provides the active sidebar section ("company" | "supplier").
 *
 * On the supplier instance the section is locked to "supplier" and
 * `canToggle` is false (the header should hide its switch). On the
 * company instance the section persists in localStorage so it survives
 * route changes and page reloads.
 */
export const SidebarSectionProvider = ({ children }) => {
  const [section, setSectionState] = useState(() => {
    if (isSupplierInstance) return SUPPLIER;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === SUPPLIER ? SUPPLIER : COMPANY;
  });

  const setSection = useCallback((next) => {
    if (isSupplierInstance) return;
    const value = next === SUPPLIER ? SUPPLIER : COMPANY;
    localStorage.setItem(STORAGE_KEY, value);
    setSectionState(value);
  }, []);

  const toggleSection = useCallback(() => {
    if (isSupplierInstance) return;
    setSectionState((prev) => {
      const next = prev === SUPPLIER ? COMPANY : SUPPLIER;
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ section, setSection, toggleSection, canToggle: !isSupplierInstance }),
    [section, setSection, toggleSection]
  );

  return (
    <SidebarSectionContext.Provider value={value}>
      {children}
    </SidebarSectionContext.Provider>
  );
};

export const useSidebarSection = () => useContext(SidebarSectionContext);

export const SIDEBAR_SECTIONS = { COMPANY, SUPPLIER };
